import { useState, useEffect, useMemo } from "react";
import {
  Typography,
  Box,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Grid,
  FormHelperText,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  updateProfeciencyRequest,
  userSkillsRequest
} from "../../../remote/usersRequests";
import SkillsList from "./SkillsList";
import theme from "../../../components/LayoutComponents/theme";
import SubmitStatememt from "../../../components/FormComponents/SubmitStatement";
import PurpleButton from "../../../components/LayoutComponents/PurpleButton";
import LoadingCircle from "../../../components/LayoutComponents/LoadingCircle";
import { deleteSourceRequest } from "../../../remote/usersRequests";
import ProcessResumeButton from "../../../components/FormComponents/ProcessResumeButton";

const styles = {
  title: {
    fontWeight: theme.typography.fontWeightBold,
    textAlign: 'center',
    color: theme.colors.purple,
  },
  statementBoxNButton: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    justifyContent: "center",
    alignItems: "center",
    gap: "1rem",
    mb: "1rem",
  },
  filtersRow: {
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "center",
    width: "100%",
    margin: "0 auto",
  },
  filter: {
    flex: "1 1 30%",
    minWidth: "200px",
  },
  skillsContainer: {
    width: '100%',
    maxHeight: "60vh",
    padding: "1rem",

  },
  updateProficiencyButton: {
    display: "flex",
    justifyContent: "center",
    margin: "1rem 0",
    gap: 2
  },
};

function MemberSkills({
  session,
  email,
  skills,
  setSkills,
  handleSkillsUpdate,
  proficiencies,
  isMobile
}) {

  const [foundationalFilter, setFoundationalFilter] = useState("");
  const [compTypeFilter, setCompTypeFilter] = useState("");
  const [profRatingFilter, setProfRatingFilter] = useState("");
  const [sourceFilter, setSourceFilter] = useState("");
  const [newlyAddedSkills, setNewlyAddedSkills] = useState([]);
  const [changedProficiencies, setChangedProficiencies] = useState([]);
  const [loading, setLoading] = useState(false)
  const [resumeModalOpen, setResumeModalOpen] = useState(false);
  const [resumeData, setResumeData] = useState(null);


  const uniqueFoundationalOrIndustry = useMemo(
    () => [...new Set(skills.map(skill => skill.competencyType))].sort(),
    [skills]
  );

  const uniqueCompTypes = useMemo(
    () => [...new Set(
      skills
        .filter(skill => !foundationalFilter || skill.competencyType === foundationalFilter)
        .map(skill => skill.competencyTier)
    )].sort(),
    [skills, foundationalFilter]
  );

  const uniqueProfRatings = useMemo(
    () => [...new Set(
      skills
        .filter(skill => !compTypeFilter || skill.competencyTier === compTypeFilter)
        .map(skill => skill?.proficiencyRating)
    )].sort((a, b) => a - b),
    [skills, compTypeFilter]
  );

  const uniqueSources = useMemo(
    () => [...new Set(skills.map(skill => skill.originalStatement).filter(Boolean))],
    [skills]
  );

  const handleProficiencyChange = (compXwalkId, compId, newProfRating) => {
    setChangedProficiencies((prev) => {
      const existingIndex = prev.findIndex(item => item.compXwalkId === compXwalkId);
      const updatedProficiency = {
        userId: session.id,
        compXwalkId,
        compId,
        proficiencyRating: newProfRating,
        modified: true,
      };
      if (existingIndex !== -1) {
        const updated = [...prev];
        updated[existingIndex] = updatedProficiency;
        return updated;
      }
      return [...prev, updatedProficiency];
    });

    setSkills((prevSkills) =>
      prevSkills.map(skill =>
        skill.compXwalkId === compXwalkId
          ? { ...skill, proficiencyRating: newProfRating, modified: true }
          : skill
      )
    );
  };

  const submitProficiencies = async () => {
    if (!changedProficiencies.length) {
      alert("No changes detected.");
      return;
    }
    try {
      await updateProfeciencyRequest(session.token, changedProficiencies);
      alert("Proficiencies updated successfully!");

      const skillsResponse = await userSkillsRequest(session.token, session.userId);
      const updatedSkills = skills?.map(skill =>
        skillsResponse.data.find(s => s.compXwalkId === skill.compXwalkId)
      ).filter(Boolean);

      setSkills(updatedSkills);
      handleSkillsUpdate(updatedSkills);
      setChangedProficiencies([]);
    } catch (error) {
      console.error("Error updating proficiencies:", error);
      alert("An error occurred while updating proficiencies. Please try again.");
    }
  };

  const filteredSkills = useMemo(() => {
    return skills?.filter(skill => {
      const matchesFoundational = !foundationalFilter || skill?.competencyType === foundationalFilter;
      const matchesCompType = !compTypeFilter || skill?.competencyTier === compTypeFilter;
      const matchesProfRating = !profRatingFilter || skill?.proficiencyRating === Number(profRatingFilter);
      const matchesSource = !sourceFilter || skill?.originalStatement === sourceFilter;
      return matchesFoundational && matchesCompType && matchesProfRating && matchesSource;
    });
  }, [skills, foundationalFilter, compTypeFilter, profRatingFilter, sourceFilter]);

  useEffect(() => {
    if (!foundationalFilter) {
      setCompTypeFilter("");
      setProfRatingFilter("");
      setSourceFilter("");
    }
  }, [foundationalFilter]);

  const handleDelete = async () => {
    if (
      window.confirm(
        'Are you sure you want to delete this source and all of it corrsponding matches? This action cannot be undone.'
      )) {
      try {
        setLoading(true)
        let res = await deleteSourceRequest(session?.token, sourceFilter)
        setSkills(res.data || [])
        alert('Deleted All matches associated with source: ' + sourceFilter)
        setSourceFilter("")
      } catch (e) {
        alert('Delete match failed, try again.')
      } finally {
        setLoading(false)
      }
    }
  }
  const FiltersUI = (
    <Grid container spacing={2} sx={styles.filtersRow}>
      <Grid item xs={12} sm={6} md={4}>
        <FormControl fullWidth size={isMobile ? "small" : "medium"}>
          <InputLabel>Foundational/Industry</InputLabel>
          <Select
            value={foundationalFilter}
            onChange={(e) => setFoundationalFilter(e.target.value)}
          >
            <MenuItem value="">All</MenuItem>
            {uniqueFoundationalOrIndustry.map((value) => (
              <MenuItem key={value} value={value}>{value}</MenuItem>
            ))}
          </Select>
          <FormHelperText>Filter by competency tier.</FormHelperText>
        </FormControl>
      </Grid>

      <Grid item xs={12} sm={6} md={4}>
        <FormControl fullWidth size={isMobile ? "small" : "medium"}>
          <InputLabel>Competency Type</InputLabel>
          <Select
            value={compTypeFilter}
            onChange={(e) => setCompTypeFilter(e.target.value)}
          >
            <MenuItem value="">All</MenuItem>
            {uniqueCompTypes.map((value) => (
              <MenuItem key={value} value={value}>{value}</MenuItem>
            ))}
          </Select>
          <FormHelperText>Filter by competency type.</FormHelperText>
        </FormControl>
      </Grid>

      <Grid item xs={12} sm={6} md={4}>
        <FormControl fullWidth size={isMobile ? "small" : "medium"}>
          <InputLabel>Proficiency Rating</InputLabel>
          <Select
            value={profRatingFilter}
            onChange={(e) => setProfRatingFilter(e.target.value)}
          >
            <MenuItem value="">All</MenuItem>
            {uniqueProfRatings.map((value) => (
              <MenuItem key={value} value={value}>{value}</MenuItem>
            ))}
          </Select>
          <FormHelperText>Filter by proficiency rating.</FormHelperText>
        </FormControl>
      </Grid>

      <Grid item xs={12} sm={6} md={4}>
        <FormControl fullWidth size={isMobile ? "small" : "medium"}>
          <InputLabel>Source</InputLabel>
          <Select
            value={sourceFilter}
            onChange={(e) => setSourceFilter(e.target.value)}
            MenuProps={{
              PaperProps: {
                style: { maxWidth: 400, minWidth: 100, whiteSpace: "normal", wordBreak: "break-word" },
              },
            }}
            renderValue={(selected) => {
              if (!selected) return "All";
              return selected.length > 50 ? selected.slice(0, 50) + "..." : selected;
            }}
          >
            <MenuItem value="">All</MenuItem>
            {uniqueSources.map((value) => (
              <MenuItem key={value} value={value} style={{ whiteSpace: "normal", wordBreak: "break-word" }}>
                {value}
              </MenuItem>
            ))}
          </Select>
          <FormHelperText>Filter by source (original statement).</FormHelperText>
        </FormControl>
      </Grid>
    </Grid>
  );

  return (
    <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'left',
            justifyContent: 'center',
          }}
        >
      <Typography variant={isMobile ? "h5" : "h4"} sx={styles.title}>
        Skills
      </Typography>

      <Box sx={styles.statementBoxNButton}>
        <SubmitStatememt
          session={session}
          email={email}
          setSkills={setSkills}
          setNewlyAddedSkills={setNewlyAddedSkills}
        />
      </Box>

      {!skills ? (
        <Box display="flex" justifyContent="center" mt={4}><LoadingCircle /></Box>
      ) : skills.length === 0 ? (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column' }}>
          <Typography variant={isMobile ? "body1" : "h5"} color={theme.colors.purple}>
            OR
          </Typography>
          <Typography variant={isMobile ? "body1" : "h5"} color={theme.colors.purple}>
            Upload your resume so that we can process it and match you to the model.
          </Typography>
          <ProcessResumeButton
            session={session}
            handleSkillsUpdate={handleSkillsUpdate}
          />
        </Box>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", width: "100%", gap: isMobile ? 2 : 0 }}>
          {isMobile ? (
            <Accordion sx={{ width: "100%" }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography>Filters</Typography>
              </AccordionSummary>
              <AccordionDetails>{FiltersUI}</AccordionDetails>
            </Accordion>
          ) : (
            FiltersUI
          )}

          <Box sx={styles.updateProficiencyButton}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
              <ProcessResumeButton
                session={session}
                handleSkillsUpdate={handleSkillsUpdate}
                resumeModalOpen={resumeModalOpen}
                setResumeModalOpen={setResumeModalOpen}
                resumeData={resumeData}
                setResumeData={setResumeData}
              />
              <FormHelperText>Once you change any proficiency level, click here to apply the changes.</FormHelperText>
              <PurpleButton onClick={submitProficiencies} label="Update Proficiencies" fullWidth={isMobile} />
              {sourceFilter !== "" && (<PurpleButton onClick={() => handleDelete('source')} label={'Delete All filtered matches?'} />)}
            </Box>
            
          </Box>
          {loading ? (<LoadingCircle />)
            :
            (<Box sx={styles.skillsContainer}>
              <SkillsList
                session={session}
                proficiencies={proficiencies}
                handleProficiencyChange={handleProficiencyChange}
                skills={filteredSkills}
                newlyAddedSkills={newlyAddedSkills}
                setSkills={setSkills}
                isMobile={isMobile}
                loading={loading}
                setLoading={setLoading}
              />
            </Box>)}
        </Box>
      )}
    </Box>
  );
}

export default MemberSkills;
